import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { FleetEvent, FleetState, TripState } from "../types";

const PATH_CAP = 5000;

const initial: FleetState = {
  now: 0,
  start: 0,
  end: 0,
  speed: 1,
  playing: false,
  pointer: 0,
  events: [],
  trips: {},
  detailedAlerts: [], // 👈 new field
};

function ensureTrip(state: FleetState, tripId: string) {
  if (!state.trips[tripId]) {
    state.trips[tripId] = {
      status: "ongoing",
      progress: 0,
      alerts: 0,
      issues: 0,
      overspeedCount: 0,
      refuels: 0,
      stops: 0,
      signalLosses: 0,
      vehicleId: undefined,
      speedKph: 0,
      fuel: 100,
      batteryPct: undefined,
      signal: "ok",
      distanceKm: 0,
      totalDistanceKm: undefined,
      odometerKm: undefined,
      last: undefined,
      path: [],
    };
  }
  return state.trips[tripId];
}

function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function updatePos(t: TripState, e: FleetEvent) {
  if (typeof e.lat === "number" && typeof e.lon === "number") {
    const prev = t.last;
    const next = { lat: e.lat, lon: e.lon, ts: e.ts };
    if (prev) {
      const d = haversineKm(prev, next);
      if (Number.isFinite(d)) t.distanceKm += d;
    }
    t.last = next;
    t.path.push([next.lat, next.lon]);
    if (t.path.length > PATH_CAP) t.path.shift();
  }
}

function updateProgress(t: TripState, currentTs: number) {
  if (typeof t.distanceKm === "number" && typeof t.totalDistanceKm === "number" && t.totalDistanceKm > 0) {
    t.progress = Math.min(100, Math.round((t.distanceKm / t.totalDistanceKm) * 100));
    return;
  }
  if (!t.startTs) return;
  const endRef = t.endTsActual ?? t.endTsPlanned ?? currentTs;
  if (endRef <= t.startTs) return;
  const pct = ((currentTs - t.startTs) / (endRef - t.startTs)) * 100;
  t.progress = Math.max(t.progress, Math.round(Math.max(0, Math.min(100, pct))));
}

function applyEvent(state: FleetState, e: FleetEvent) {
  const t = ensureTrip(state, e.tripId);
  const kind = e.type;

  if (e.vehicleId) t.vehicleId = e.vehicleId;
  if (typeof e.speedKph === "number") t.speedKph = e.speedKph;
  if (typeof e.fuelPct === "number") t.fuel = e.fuelPct;
  if (typeof e.batteryPct === "number") t.batteryPct = e.batteryPct;
  if (typeof e.distanceTotalKm === "number") t.totalDistanceKm = e.distanceTotalKm;
  if (typeof e.odometerKm === "number") t.odometerKm = e.odometerKm;

  updatePos(t, e);

  if (kind === "trip_started") {
    t.status = "ongoing";
    t.startTs = e.ts;
    t.started = true;
    t.progress = 0;
    t.alerts = 0;
    t.issues = 0;
    t.overspeedCount = 0;
    t.refuels = 0;
    t.stops = 0;
    t.signalLosses = 0;
    t.signal = "ok";
    t.distanceKm = 0;
  } else if (kind === "trip_completed") {
    t.status = "completed";
    t.endTsActual = e.ts;
    t.endTsPlanned = t.endTsPlanned ?? e.ts;
    t.progress = 100;
  } else if (kind === "trip_cancelled") {
    t.status = "cancelled";
    t.endTsActual = e.ts;
    t.progress = Math.min(t.progress, 99);
  }

  // ---- Alerts / Issues ----
  const alertTypes = [
    "trip_cancelled",
    "signal_lost",
    "vehicle_stopped",
    "speed_violation",
    "device_error",
    "battery_low",
    "fuel_level_low",
  ];

  if (alertTypes.includes(kind)) {
    const alertMsgMap: Record<string, string> = {
      trip_cancelled: "Trip cancelled unexpectedly",
      signal_lost: "Signal lost from device",
      vehicle_stopped: "Vehicle stopped during trip",
      speed_violation: "Speed limit exceeded",
      device_error: "Device reported an error",
      battery_low: "Low battery warning",
      fuel_level_low: "Low fuel level detected",
    };

    const details = {
      vehicleId: e.vehicleId ?? t.vehicleId ?? "Unknown",
      tripId: e.tripId,
      type: kind,
      ts: e.ts,
      message: alertMsgMap[kind] ?? kind,
      speed: e.speedKph ?? t.speedKph,
      fuel: e.fuelPct ?? t.fuel,
      battery: e.batteryPct ?? t.batteryPct,
      location: e.lat && e.lon ? `${e.lat.toFixed(3)}, ${e.lon.toFixed(3)}` : "—",
      status: t.status,
    };

    state.detailedAlerts.unshift(details);
    if (state.detailedAlerts.length > 100) state.detailedAlerts.pop();
  }

  // --- Metrics updates ---
  if (kind === "speed_violation" || e.overspeed) {
    t.alerts++;
    t.overspeedCount++;
  }
  if (kind === "battery_low" || kind === "fuel_level_low") t.alerts++;
  if (kind === "signal_lost" || e.signalLost) {
    t.signalLosses++;
    t.signal = "lost";
    if (t.status !== "completed") t.status = "issue";
  }
  if (kind === "signal_recovered" || e.signalRecovered) t.signal = "ok";
  if (kind === "vehicle_stopped") t.stops++;
  if (kind === "refueling_started") t.refuels++;
  if (kind === "refueling_completed" && typeof e.fuelPct === "number") t.fuel = e.fuelPct;
  if (kind === "device_error") {
    t.issues++;
    if (t.status !== "completed") t.status = "issue";
  }

  if (t.status === "ongoing") updateProgress(t, e.ts);
}

const slice = createSlice({
  name: "fleet",
  initialState: initial,
  reducers: {
    init(state, { payload }: PayloadAction<{ events: FleetEvent[]; start: number; end: number }>) {
      const { events, start, end } = payload;
      state.events = events;
      state.start = start;
      state.end = end;
      state.now = start;
      state.pointer = 0;
      state.trips = {};
      const plannedEnd = new Map<string, number>();
      const plannedStart = new Map<string, number>();
      for (const e of events) {
        if (e.type === "trip_started") plannedStart.set(e.tripId, e.ts);
        if (e.type === "trip_completed")
          plannedEnd.set(e.tripId, Math.max(plannedEnd.get(e.tripId) ?? 0, e.ts));
      }
      plannedStart.forEach((startTs, tripId) => {
        const t = ensureTrip(state, tripId);
        t.startTs = startTs;
        const endTs = plannedEnd.get(tripId);
        if (endTs) t.endTsPlanned = endTs;
      });
    },

    tick(state, { payload }: PayloadAction<{ dtMs: number }>) {
      state.now += payload.dtMs;
      while (state.pointer < state.events.length && state.events[state.pointer].ts <= state.now) {
        applyEvent(state, state.events[state.pointer]);
        state.pointer++;
      }
    },

    setPlaying(state, { payload }: PayloadAction<boolean>) {
      state.playing = payload;
    },

    setSpeed(state, { payload }: PayloadAction<1 | 5 | 50 | 100 | 1000>) {
      state.speed = payload;
    },

    reset(state) {
      state.now = state.start;
      state.pointer = 0;
      state.trips = {};
      state.playing = false;
      state.detailedAlerts = [];
    },

    setFleetNow(state, { payload }: PayloadAction<number>) {
      const target = Math.min(Math.max(payload, state.start), state.end);
      if (target < state.now) {
        state.now = state.start;
        state.pointer = 0;
        state.trips = {};
      }
      let lo = state.pointer,
        hi = state.events.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (state.events[mid].ts <= target) lo = mid + 1;
        else hi = mid;
      }
      const upTo = lo;
      while (state.pointer < upTo) {
        applyEvent(state, state.events[state.pointer]);
        state.pointer++;
      }
      state.now = target;
    },
  },
});

export const { init, tick, setPlaying, setSpeed, reset, setFleetNow } = slice.actions;
export default slice.reducer;
